import type { TFunction } from "i18next";
import type { TranslationKeys } from "../../../i18n/types";
import {
	type BreakRulesConfig,
	type BreakRuleType,
	EmploymentType,
	type HoursRateConfig,
	NurseRole,
	type OvertimeConfig,
	OvertimeConfigType,
	RateHoursBonusType,
} from "../../../utils/models";
import { getEmploymentTypeDisplayName, nurseRoleDisplayName } from "../../../utils/utilFunctions";
import type { BreakRulesColumnId, HoursRateColumnId, OvertimeColumnId } from "./wageRuleColumns";

/*
 * DAT-906 · A stored Wage Settings row as the text its table shows, column by column — for surfaces that
 * show the rows read-only (the wage breakdown's hover card). Rows are in the STORED shape, where `[]` means
 * "All". The wording is the tables' own option labels, so a value reads the same in both places.
 */

/*
 * The rows these read: the stored models, with the enum-typed lists widened to strings so a rule's scope as
 * it was captured on a priced shift (plain strings, which survive a rename) reads through the same code.
 * Neither id nor department is shown.
 */
type Widened<TRow, TKeys extends keyof TRow> = Omit<TRow, TKeys | "id" | "departmentId"> & { [K in TKeys]?: readonly string[] };
export type HoursRateCellRow = Widened<HoursRateConfig, "roles" | "trainingStatuses" | "employmentTypes">;
export type OvertimeCellRow = Widened<OvertimeConfig, "roles">;
export type BreakRulesCellRow = Widened<BreakRulesConfig, "roles" | "employmentTypes">;

export type RuleCellFormat = {
	t: TFunction;
	/** A rate in dollars as this viewer may see it — the caller applies its own wage-hiding rule. */
	money: (dollars: number) => string;
	/** A job's display name. */
	jobLabel: (jobId: string) => string;
};

const LIST_SEPARATOR = ", ";
const PART_SEPARATOR = " · ";
const ALL_KEY: TranslationKeys = "department.config.rates.option.all";

const isNurseRole = (value: string): value is NurseRole => Object.values<string>(NurseRole).includes(value);
const isEmploymentType = (value: string): value is EmploymentType => Object.values<string>(EmploymentType).includes(value);

const roleLabel = (role: string): string => (isNurseRole(role) ? (nurseRoleDisplayName[role] ?? role) : role);
const employmentTypeLabel = (value: string): string => (isEmploymentType(value) ? getEmploymentTypeDisplayName(value) : value);

/**
 * A multi-select cell. DEFAULT: `undefined` reads as "All" like `[]` — the stored shape never leaves a
 * list unset (only an unsaved draft row does), and the models document both as "All".
 */
const listText = (f: RuleCellFormat, values: readonly string[] | undefined, labelOf: (value: string) => string): string =>
	values === undefined || values.length === 0 ? f.t(ALL_KEY) : [...new Set(values.map(labelOf))].join(LIST_SEPARATOR);

/** A tri-state cell (`null`/unset = All). */
const triStateText = (f: RuleCellFormat, value: boolean | null | undefined, whenTrue: TranslationKeys, whenFalse: TranslationKeys): string =>
	value === true ? f.t(whenTrue) : value === false ? f.t(whenFalse) : f.t(ALL_KEY);

/** The union scope, then the specific unions when members of only some are meant — the table's two dropdowns. */
const unionText = (f: RuleCellFormat, isUnion: boolean | null | undefined, union: readonly string[] | undefined): string => {
	const scope = triStateText(f, isUnion, "department.config.rates.union.membersOnly", "department.config.rates.union.nonUnionOnly");
	return isUnion === true && union !== undefined && union.length > 0 ? [scope, union.join(LIST_SEPARATOR)].join(PART_SEPARATOR) : scope;
};

/** A rate cell: the table's `$`/`%` adornment beside the value; money goes through the caller's hiding rule. */
const rateText = (f: RuleCellFormat, value: number, type: RateHoursBonusType): string =>
	type === RateHoursBonusType.Percentage ? `${value}%` : f.money(value);

const rateTypeText = (f: RuleCellFormat, type: RateHoursBonusType): string =>
	f.t(type === RateHoursBonusType.Percentage ? "department.config.rates.wageMode.percentage" : "department.config.rates.wageMode.flatRate");

/** A stored time as the table shows it: "18:00:00" and "18:00" both read "18:00". */
export const clockTime = (time: string): string => time.split(":").slice(0, 2).join(":");

const OVERTIME_TYPE_KEY: Readonly<Record<OvertimeConfigType, TranslationKeys>> = {
	[OvertimeConfigType.weekly]: "department.config.rates.overtime.type.weekly",
	[OvertimeConfigType.daily]: "department.config.rates.overtime.type.daily",
	[OvertimeConfigType.all]: ALL_KEY,
	[OvertimeConfigType.consecutive_day]: "department.config.rates.overtime.type.consecutiveDay",
};

const BREAK_TYPE_KEY: Readonly<Record<BreakRuleType, TranslationKeys>> = {
	addition: "department.config.rates.breakRules.type.addition",
	override: "department.config.rates.breakRules.type.override",
};

export const BREAK_PAY_KEY: Readonly<Record<"paid" | "unpaid", TranslationKeys>> = {
	paid: "department.config.rates.breakRules.pay.paid",
	unpaid: "department.config.rates.breakRules.pay.unpaid",
};

export const hoursRateCellText = (f: RuleCellFormat, row: HoursRateCellRow): Record<HoursRateColumnId, string> => ({
	name: row.name,
	hours: row.startTime && row.endTime ? `${clockTime(row.startTime)}-${clockTime(row.endTime)}` : f.t("all.day"),
	role: listText(f, row.roles, roleLabel),
	job: listText(f, row.jobIds, f.jobLabel),
	rate: rateText(f, row.bonusVal, row.bonusType),
	rateType: rateTypeText(f, row.bonusType),
	charge: triStateText(f, row.isCharge, "department.config.rates.option.yes", "department.config.rates.option.no"),
	trainingStatus: listText(f, row.trainingStatuses, (status) => status),
	employmentType: listText(f, row.employmentTypes, employmentTypeLabel),
	weekend: triStateText(f, row.weekend, "department.config.rates.option.weekend", "department.config.rates.option.weekday"),
	union: unionText(f, row.isUnion, row.union),
	agency: listText(f, row.agency, (agency) => agency),
});

/** The streak's day range shows only on a consecutive-day rule, as in the table; an open end reads "Onward". */
const consecutiveDays = (f: RuleCellFormat, row: OvertimeCellRow): Pick<Record<OvertimeColumnId, string>, "daysFrom" | "daysTo"> => {
	if (row.type !== OvertimeConfigType.consecutive_day) return { daysFrom: "", daysTo: "" };
	return {
		daysFrom: row.consecutiveDaysFrom == null ? "" : String(row.consecutiveDaysFrom),
		daysTo: row.consecutiveDaysTo == null ? f.t("department.config.rates.overtime.daysTo.onward") : String(row.consecutiveDaysTo),
	};
};

export const overtimeCellText = (f: RuleCellFormat, row: OvertimeCellRow): Record<OvertimeColumnId, string> => ({
	name: row.name,
	hoursLimit: String(row.hoursLimit),
	role: listText(f, row.roles, roleLabel),
	job: listText(f, row.jobIds, f.jobLabel),
	employmentType: listText(f, row.employmentType, employmentTypeLabel),
	union: unionText(f, row.isUnion, row.union),
	type: f.t(OVERTIME_TYPE_KEY[row.type]),
	...consecutiveDays(f, row),
	rate: rateText(f, row.rate, row.rateType),
	rateType: rateTypeText(f, row.rateType),
});

export const breakRulesCellText = (f: RuleCellFormat, row: BreakRulesCellRow): Record<BreakRulesColumnId, string> => ({
	name: row.name,
	breakLimit: String(row.breakLimit),
	// DEFAULT: an empty break start IS the break limit (the table's placeholder says so), so the hours that apply show.
	breakStart: String(row.breakStart ?? row.breakLimit),
	role: listText(f, row.roles, roleLabel),
	job: listText(f, row.jobIds, f.jobLabel),
	employmentType: listText(f, row.employmentTypes, employmentTypeLabel),
	union: unionText(f, row.isUnion, row.union),
	type: f.t(BREAK_TYPE_KEY[row.type]),
	breakTime: String(row.breakTime),
	pay: f.t(BREAK_PAY_KEY[row.isPaid ? "paid" : "unpaid"]),
});
